import { Button, Flex, Progress } from "antd";
import { useState } from "react";
import {
  useGetQuizAnswerMutation,
  useQuizSubmitMutation,
} from "../../../../app/features/courses/courses.api";
import type {
  IQuizOption,
  IQuizResultResponse,
} from "../../../../app/features/courses/courses.types";
import useError from "../../../../hooks/useError";
import { cn } from "../../../../utils/global.utils";
import { enToBnNumber } from "../../../../utils/number.utils";
import QuizContentLoading from "./QuizContentLoading";
import QuizQuestions from "./QuizQuestions";
import SubmittedQuiz from "./SubmittedQuiz";

/* ================= TYPES ================= */

type Question = {
  id: number;
  question: string;
  options: IQuizOption[];
};

type Answers = Record<string, number>;

type Props = {
  questions: Question[];
  title: string;
  onComplete?: () => void;
  courseId: string;
  quizId: string;
  isCompleted: boolean;
  quizResult?: IQuizResultResponse;
  onPrevious?: () => void;
};

/* ================= COMPONENT ================= */

export default function QuizContent({
  questions,
  title,
  onComplete,
  courseId,
  quizId,
  isCompleted,
  quizResult,
  onPrevious,
}: Props) {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [submitted, setSubmitted] = useState<boolean>(false);

  const [submitQuiz, { isLoading, error, data: submissionResult }] =
    useQuizSubmitMutation();
  const [
    getQuizAnswer,
    { isLoading: getAnswerLoading, error: getAnswerError },
  ] = useGetQuizAnswerMutation();

  useError(error, "কুইজ জমা দিতে সমস্যা হয়েছে");
  useError(getAnswerError, "উত্তর যাচাই করতে সমস্যা হয়েছে");

  const [validationState, setValidationState] = useState<
    Record<string, { isCorrect: boolean }>
  >({});

  const currentQuestion = questions[currentIndex];

  /* ================= HANDLERS ================= */

  const handleAnswerChange = async (questionId: string, value: number) => {
    // value is option.id
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));

    try {
      const response = await getQuizAnswer({
        courseId,
        quizId: Number(quizId),
        questionId: questionId,
        selectedOptionIds: [value],
      }).unwrap();

      setValidationState((prev) => ({
        ...prev,
        [questionId]: { isCorrect: response.isCorrect },
      }));
    } catch (err) {
      console.error("Answer validation failed", err);
    }
  };

  const handleSubmit = async () => {
    try {
      await submitQuiz({
        courseId,
        quizId: Number(quizId),
        answersJson: JSON.stringify(answers),
      }).unwrap();
      setSubmitted(true);
      onComplete?.();
    } catch (err) {
      console.error("Quiz submission failed", err);
    }
  };

  const currentValidation = validationState[currentQuestion.id.toString()];
  const isCorrect = currentValidation?.isCorrect;

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      // Logic for last question handled by handleSubmit now
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    } else {
      if (onPrevious) onPrevious();
    }
  };

  /* ================= RENDER ================= */

  if ((submitted || isCompleted) && (submissionResult || quizResult?.attempt)) {
    const result = submissionResult || quizResult?.attempt;
    return (
      <SubmittedQuiz
        scorePercent={result?.scorePercent || 0}
        correctCount={result?.totalMarks || 0}
        wrongCount={result?.wrongCount || 0}
      />
    );
  }

  return (
    <section>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-theme-slate-700 text-2xl font-semibold">{title}</h3>
      </div>

      {isLoading ? (
        <QuizContentLoading />
      ) : (
        <>
          {/* ================= HEADER ================= */}
          <div className="p-5 bg-white border border-theme-zinc-200 rounded-xl mb-4">
            <Flex justify="space-between" className="mb-2">
              <div>
                <h4 className="text-theme-zinc-700 text-sm font-medium mb-1">
                  কুইজ টেস্ট
                </h4>
                <p className="text-theme-slate-500 text-xs font-normal leading-4">
                  আপনার জ্ঞান যাচাই করার জন্য এই কুইজটি দিন।
                </p>
              </div>

              {/* Timer can be re-implemented if backend provides duration */}
              {/* <p className="text-theme-yellow-700 text-sm font-medium">
                <span className="relative top-[1px] mr-1">⏱</span> সময় বাকি আছে.
                ৩০:০০ মিনিট
              </p> */}
            </Flex>
            <div className="relative top-1">
              <Flex justify="space-between" className="relative top-1">
                <span className="text-theme-slate-600 text-sm">
                  {enToBnNumber(currentIndex + 1)}/
                  {enToBnNumber(questions.length)} প্রশ্ন
                </span>

                <span className="text-theme-slate-600 text-sm">
                  {enToBnNumber(Object.keys(answers).length)} টি উত্তর দেওয়া
                  হয়েছে
                </span>
              </Flex>

              <Progress
                percent={Math.round(
                  (Object.keys(answers).length / questions.length) * 100
                )}
                showInfo={false}
                strokeColor="#207E7D"
                trailColor="#E9EFF6"
              />
            </div>
          </div>
          <QuizQuestions
            currentQuestion={currentQuestion}
            answers={answers}
            submitted={submitted}
            handleAnswerChange={handleAnswerChange}
            validationResult={currentValidation}
            isChecking={getAnswerLoading}
          />
        </>
      )}

      {/* ================= NAVIGATION ================= */}
      <Flex justify="space-between" className="mt-6">
        <Button
          variant="outlined"
          type="text"
          size="middle"
          onClick={handlePrevious}
          disabled={
            (currentIndex === 0 && !onPrevious) || isLoading || getAnswerLoading
          }
          className={cn(
            "rounded-lg min-w-24",
            currentIndex !== 0 || (currentIndex === 0 && onPrevious)
              ? "!text-theme-primary !bg-[#E9F2F2] hover:!bg-[#d3e6e6]"
              : "opacity-90 cursor-not-allowed"
          )}
        >
          পূর্ববর্তী
        </Button>

        <Button
          type="primary"
          onClick={
            currentIndex === questions.length - 1 ? handleSubmit : handleNext
          }
          loading={isLoading}
          disabled={
            !answers[currentQuestion.id] || !isCorrect || getAnswerLoading
          }
        >
          {currentIndex === questions.length - 1 ? "জমা দিন" : "পরবর্তী"}
        </Button>
      </Flex>
    </section>
  );
}
